import type { PredicateNode } from '@/admin/components/condition-builder/types'
import type {
  RuleHit,
  ValidationRule,
} from '@/admin/hooks/use-validation-rules'

interface LocalRuleContext {
  fields?: Record<string, unknown>
  entities?: Record<string, Record<string, unknown>>
  aggregates?: Record<string, unknown>
  roles?: string[]
  permissions?: string[]
}

interface ParsedRefField {
  kind: 'field'
  codeName: string
}

interface ParsedRefEntity {
  kind: 'entity'
  entity: string
  path: string[]
}

interface ParsedRefAggregate {
  kind: 'aggregate'
  key: string
}

interface ParsedRefPermission {
  kind: 'permission'
  codeName: string
}

interface ParsedRefRole {
  kind: 'role'
  codeName: string
}

interface ParsedRefComputedDateDiff {
  kind: 'computed'
  fn: 'date_diff'
  left: ParsedRef
  right: ParsedRef
  unit: 'days' | 'months' | 'years'
}

type ParsedRef =
  | ParsedRefField
  | ParsedRefEntity
  | ParsedRefAggregate
  | ParsedRefPermission
  | ParsedRefRole
  | ParsedRefComputedDateDiff

interface EvaluationResult {
  matched: boolean
  supported: boolean
}

export interface LocalValidationEvaluation {
  hits: RuleHit[]
  unsupportedRuleUuids: string[]
}

export function evaluateValidationRulesLocally(
  rules: ValidationRule[],
  context: LocalRuleContext,
): LocalValidationEvaluation {
  const hits: RuleHit[] = []
  const unsupportedRuleUuids: string[] = []

  for (const rule of rules) {
    if (!rule.is_active || !rule.conditions) continue

    const result = evaluatePredicate(rule.conditions, context)
    if (!result.supported) {
      unsupportedRuleUuids.push(rule.uuid)
      continue
    }

    if (!result.matched) continue

    hits.push({
      rule_uuid: rule.uuid,
      name: rule.name,
      severity: rule.severity,
      message: rule.message || rule.name,
      trigger_step: rule.trigger_step,
    })
  }

  return { hits, unsupportedRuleUuids }
}

export function evaluatePredicateLocally(
  predicate: PredicateNode | null,
  context: LocalRuleContext,
): EvaluationResult {
  if (!predicate) {
    return { matched: false, supported: true }
  }

  return evaluatePredicate(predicate, context)
}

function evaluatePredicate(
  predicate: PredicateNode,
  context: LocalRuleContext,
): EvaluationResult {
  if (predicate.type === 'group') {
    if (predicate.operator !== 'and') {
      return { matched: false, supported: false }
    }

    for (const child of predicate.children) {
      const result = evaluatePredicate(child, context)
      if (!result.supported) return result
      if (!result.matched) return { matched: false, supported: true }
    }

    return { matched: true, supported: true }
  }

  if (predicate.ref.startsWith('lookup:') || predicate.op === 'already_exists') {
    return { matched: false, supported: false }
  }

  const parsedRef = parseRef(predicate.ref)
  if (!parsedRef) return { matched: false, supported: false }

  const resolved = resolveRef(parsedRef, context)
  if (resolved === MISSING) {
    return { matched: false, supported: true }
  }

  return {
    matched: applyOp(predicate.op, resolved, predicate.value),
    supported: true,
  }
}

const MISSING = Symbol('missing')

function parseRef(raw: string): ParsedRef | null {
  const [kind, rest] = raw.split(':', 2)
  if (!kind || !rest) return null

  if (kind === 'field') {
    return { kind: 'field', codeName: rest }
  }

  if (kind === 'entity') {
    const [entity, ...path] = rest.split('.')
    if (!entity || path.length === 0) return null
    return { kind: 'entity', entity, path }
  }

  if (kind === 'aggregate') {
    const [codeName, fn] = rest.split('.')
    if (!codeName || !fn) return null
    return { kind: 'aggregate', key: `${codeName}.${fn}` }
  }

  if (kind === 'permission') {
    return { kind: 'permission', codeName: rest }
  }

  if (kind === 'role') {
    return { kind: 'role', codeName: rest }
  }

  if (kind === 'computed') {
    const match = rest.match(/^date_diff\((.+),(.+),(days|months|years)\)$/)
    if (!match) return null

    const [, leftRaw, rightRaw, unit] = match
    const left = parseRef(leftRaw.trim())
    const right = parseRef(rightRaw.trim())
    if (!left || !right) return null

    return {
      kind: 'computed',
      fn: 'date_diff',
      left,
      right,
      unit,
    }
  }

  return null
}

function resolveRef(
  ref: ParsedRef,
  context: LocalRuleContext,
): unknown | typeof MISSING {
  if (ref.kind === 'field') {
    return context.fields?.[ref.codeName] ?? MISSING
  }

  if (ref.kind === 'entity') {
    let value: unknown = context.entities?.[ref.entity]
    if (value === undefined) return MISSING

    for (const part of ref.path) {
      if (value == null || typeof value !== 'object') return MISSING
      if (!(part in value)) return MISSING
      value = (value as Record<string, unknown>)[part]
    }

    return value
  }

  if (ref.kind === 'aggregate') {
    return context.aggregates?.[ref.key] ?? MISSING
  }

  if (ref.kind === 'permission') {
    return (context.permissions ?? []).includes(ref.codeName)
  }

  if (ref.kind === 'role') {
    return (context.roles ?? []).includes(ref.codeName)
  }

  if (ref.kind === 'computed') {
    const left = resolveRef(ref.left, context)
    const right = resolveRef(ref.right, context)
    if (left === MISSING || right === MISSING) return MISSING

    const leftDate = toDate(left)
    const rightDate = toDate(right)
    if (!leftDate || !rightDate) return MISSING

    const diffMs = leftDate.getTime() - rightDate.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    if (ref.unit === 'days') return diffDays
    if (ref.unit === 'months') return Math.floor(diffDays / 30)
    return Math.floor(diffDays / 365)
  }

  return MISSING
}

function applyOp(op: string, resolved: unknown, value: unknown): boolean {
  if (op === 'exists') {
    return resolved !== null && resolved !== undefined
  }

  const resolvedComparable = normaliseComparable(resolved)
  const valueComparable = normaliseComparable(resolveRelativeDateValue(value))

  if (op === 'eq') return resolvedComparable === valueComparable
  if (op === 'ne') return resolvedComparable !== valueComparable
  if (op === 'in') return Array.isArray(value) && value.includes(resolved)
  if (op === 'not_in') return Array.isArray(value) && !value.includes(resolved)
  if (op === 'matches') {
    if (resolved == null) return false
    try {
      return new RegExp(String(value)).test(String(resolved))
    } catch {
      return false
    }
  }
  if (op === 'not_matches') {
    if (resolved == null) return true
    try {
      return !new RegExp(String(value)).test(String(resolved))
    } catch {
      return false
    }
  }
  if (op === 'gt') return compareValues(resolvedComparable, valueComparable) > 0
  if (op === 'gte') return compareValues(resolvedComparable, valueComparable) >= 0
  if (op === 'lt') return compareValues(resolvedComparable, valueComparable) < 0
  if (op === 'lte') return compareValues(resolvedComparable, valueComparable) <= 0
  if (op === 'between') {
    if (!Array.isArray(value) || value.length !== 2) return false
    const low = normaliseComparable(resolveRelativeDateValue(value[0]))
    const high = normaliseComparable(resolveRelativeDateValue(value[1]))
    return (
      compareValues(resolvedComparable, low) >= 0 &&
      compareValues(resolvedComparable, high) <= 0
    )
  }

  return false
}

function compareValues(left: unknown, right: unknown): number {
  if (left == null || right == null) return -1
  if (typeof left === 'number' && typeof right === 'number') return left - right
  if (typeof left === 'string' && typeof right === 'string') {
    return left.localeCompare(right)
  }
  return String(left).localeCompare(String(right))
}

function normaliseComparable(value: unknown): unknown {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
      return Number(trimmed)
    }

    const date = toDate(trimmed)
    if (date) return date.getTime()

    return trimmed
  }

  if (value instanceof Date) return value.getTime()
  return value
}

function resolveRelativeDateValue(value: unknown): unknown {
  if (typeof value !== 'string') return value

  const match = value.match(/^now\s*([+-])\s*(\d+)\s*([dwMy])$/)
  if (!match) return value

  const [, sign, amountRaw, unit] = match
  const amount = Number(amountRaw) * (sign === '-' ? -1 : 1)
  const date = new Date()

  if (unit === 'd') date.setDate(date.getDate() + amount)
  if (unit === 'w') date.setDate(date.getDate() + amount * 7)
  if (unit === 'M') date.setMonth(date.getMonth() + amount)
  if (unit === 'y') date.setFullYear(date.getFullYear() + amount)

  return date.toISOString().slice(0, 10)
}

function toDate(value: unknown): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (typeof value !== 'string') return null

  const candidate = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T00:00:00`)
    : new Date(value)

  return Number.isNaN(candidate.getTime()) ? null : candidate
}
